import { Link } from 'react-router-dom';
import useInView from '../../../hooks/useInView';

const INDUSTRIES = [
  {
    icon: 'ri-store-2-line',
    title: 'Retail & E-Commerce',
    desc: 'Increase visibility, drive traffic, and convert more visitors into customers.',
    color: 'text-orange-500',
    bg: 'bg-orange-50 border-orange-100',
  },
  {
    icon: 'ri-building-2-line',
    title: 'Real Estate',
    desc: 'Generate qualified leads and build brand credibility for developers and agents.',
    color: 'text-sky-500',
    bg: 'bg-sky-50 border-sky-100',
  },
  {
    icon: 'ri-hospital-line',
    title: 'Healthcare & Wellness',
    desc: 'Reach local patients and grow your practice with the right digital presence.',
    color: 'text-brand-blue-light',
    bg: 'bg-blue-50 border-blue-100',
  },
  {
    icon: 'ri-graduation-cap-line',
    title: 'Education & EdTech',
    desc: 'Reach students, increase enrolments, and grow your learning community online.',
    color: 'text-violet-500',
    bg: 'bg-violet-50 border-violet-100',
  },
  {
    icon: 'ri-restaurant-line',
    title: 'Food & Hospitality',
    desc: 'Build a stronger local and social presence to attract and retain customers.',
    color: 'text-yellow-500',
    bg: 'bg-yellow-50 border-yellow-100',
  },
  {
    icon: 'ri-briefcase-4-line',
    title: 'B2B & Professional Services',
    desc: 'Build authority, generate leads, and communicate your value clearly online.',
    color: 'text-brand-blue',
    bg: 'bg-blue-50 border-blue-100',
  },
  {
    icon: 'ri-home-4-line',
    title: 'Home & Lifestyle',
    desc: 'Grow your audience and convert social engagement into business enquiries.',
    color: 'text-rose-500',
    bg: 'bg-rose-50 border-rose-100',
  },
  {
    icon: 'ri-rocket-line',
    title: 'Startups & Early-Stage',
    desc: 'Establish a digital presence from scratch — strategically and without complexity.',
    color: 'text-cyan-500',
    bg: 'bg-cyan-50 border-cyan-100',
  },
];

function IndustryCard({ item, delay }: { item: typeof INDUSTRIES[0]; delay: number }) {
  const { ref, visible } = useInView(0.05);
  return (
    <div
      ref={ref}
      className="group flex items-start gap-4 bg-white border border-gray-100 hover:border-gray-200 rounded-2xl p-5 transition-all duration-300 cursor-default"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(18px)',
        transition: `opacity 0.5s ease ${delay}ms, transform 0.5s ease ${delay}ms`,
      }}
    >
      <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl border ${item.bg}`}>
        <i className={`${item.icon} text-lg ${item.color}`}></i>
      </div>
      <div>
        <h3 className="font-display font-semibold text-gray-900 text-[14px] mb-1 leading-snug">{item.title}</h3>
        <p className="text-gray-500 text-[12px] leading-relaxed">{item.desc}</p>
      </div>
    </div>
  );
}

export default function IndustriesPreview() {
  const { ref: headRef, visible: headVisible } = useInView(0.2);

  return (
    <section className="py-12 md:py-16 bg-white">
      <div className="max-w-site mx-auto px-4 sm:px-6">
        <div
          ref={headRef}
          className="mb-8 md:mb-10"
          style={{
            opacity: headVisible ? 1 : 0,
            transform: headVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-brand-blue mb-5">
            Who We Work With
          </span>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
            <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-[2.6rem] text-gray-900 leading-tight max-w-lg">
              Industries We<br />Can Help
            </h2>
            <p className="text-gray-500 text-[15px] max-w-sm leading-relaxed">
              We work across several sectors. The principles of good digital marketing apply broadly — reach out if yours isn't listed.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {INDUSTRIES.map((item, i) => (
            <IndustryCard key={item.title} item={item} delay={i * 55} />
          ))}
        </div>

        <div className="flex justify-center">
          <Link
            to="/industries"
            className="btn-outline px-6 py-3"
          >
            See Detailed Breakdown
            <i className="ri-arrow-right-line text-sm"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
